import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  FaSearch,
  FaTimes,
  FaRoute,
  FaMapMarkerAlt,
  FaClock,
  FaRoad,
  FaArrowRight,
  FaTimesCircle,
  FaCheckCircle,
  FaBus,
} from "react-icons/fa";
import { routesData, originHubs } from "./routesData";
import RouteCard from "./RouteCard";
import RouteModal from "./RouteModal";
import routesHeroImage from "../Assets/images/onebusbranding.png";
import "./RoutesPage.css";

const RoutesPage = () => {
  const [activeHub, setActiveHub] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRoute, setSelectedRoute] = useState(null);

  /* ============================================================
     FILTER LOGIC
     ============================================================ */
  const filteredRoutes = useMemo(() => {
    let routes = routesData;

    // Filter by origin hub
    if (activeHub !== "All") {
      routes = routes.filter((route) =>
        route.routeName.toLowerCase().startsWith(activeHub.toLowerCase())
      );
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      routes = routes.filter((route) => {
        const inName = route.routeName.toLowerCase().includes(term);
        const inPoints = route.routePoints.some((p) =>
          p.name.toLowerCase().includes(term)
        );
        return inName || inPoints;
      });
    }

    return routes;
  }, [activeHub, searchTerm]);

  const hubCounts = useMemo(() => {
    const counts = { All: routesData.length };
    originHubs.forEach((hub) => {
      counts[hub] = routesData.filter((r) =>
        r.routeName.toLowerCase().startsWith(hub.toLowerCase())
      ).length;
    });
    return counts;
  }, []);

  const handleResetFilters = () => {
    setActiveHub("All");
    setSearchTerm("");
  };

  return (
    <div className="staffline-routes-page">

      {/* ===== HERO ===== */}
      <section
        className="staffline-routes-hero"
        style={{ backgroundImage: `url(${routesHeroImage})` }}
      >
        <div className="staffline-routes-hero-overlay"></div>
        <div className="container staffline-routes-hero-content">
          <span className="staffline-routes-hero-tag">
            <FaRoute /> Our Routes
          </span>
          <h1 className="staffline-routes-hero-title">
            Choose Your Route. <span>Ride Daily.</span>
          </h1>
          <p className="staffline-routes-hero-subtitle">
            24+ routes across Accra and Tema. One flat monthly fee of 800 GHS.
            Guaranteed seat, fixed schedule, and a stress-free commute.
          </p>
        </div>
      </section>

      {/* ===== STATS STRIP ===== */}
      <section className="staffline-routes-stats">
        <div className="container">
          <div className="staffline-routes-stats-grid">
            <div className="staffline-routes-stat">
              <FaRoute />
              <div>
                <strong>24+</strong>
                <span>Routes Available</span>
              </div>
            </div>
            <div className="staffline-routes-stat">
              <FaMapMarkerAlt />
              <div>
                <strong>6</strong>
                <span>Origin Hubs</span>
              </div>
            </div>
            <div className="staffline-routes-stat">
              <FaClock />
              <div>
                <strong>Mon – Fri</strong>
                <span>Service Days</span>
              </div>
            </div>
            <div className="staffline-routes-stat">
              <FaRoad />
              <div>
                <strong>800 GHS</strong>
                <span>Flat Monthly Fee</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FILTER BAR ===== */}
      <section className="staffline-routes-filter-section">
        <div className="container">

          {/* Search Box */}
          <div className="staffline-routes-search">
            <FaSearch className="staffline-routes-search-icon" />
            <input
              type="text"
              placeholder="Search by route, destination, or pickup point..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="staffline-routes-search-clear"
                onClick={() => setSearchTerm("")}
                aria-label="Clear search"
              >
                <FaTimes />
              </button>
            )}
          </div>

          {/* Hub Filter Buttons */}
          <div className="staffline-routes-filters">
            <button
              className={`staffline-routes-filter-btn ${
                activeHub === "All" ? "active" : ""
              }`}
              onClick={() => setActiveHub("All")}
            >
              All Routes <span>{hubCounts.All}</span>
            </button>
            {originHubs.map((hub) => (
              <button
                key={hub}
                className={`staffline-routes-filter-btn ${
                  activeHub === hub ? "active" : ""
                }`}
                onClick={() => setActiveHub(hub)}
              >
                {hub} <span>{hubCounts[hub]}</span>
              </button>
            ))}
          </div>

          {/* Results Count */}
          <div className="staffline-routes-results">
            <p>
              Showing <strong>{filteredRoutes.length}</strong> of{" "}
              <strong>{routesData.length}</strong> routes
            </p>
            {(activeHub !== "All" || searchTerm) && (
              <button
                className="staffline-routes-reset"
                onClick={handleResetFilters}
              >
                <FaTimesCircle /> Clear Filters
              </button>
            )}
          </div>

        </div>
      </section>

      {/* ===== ROUTES GRID ===== */}
      <section className="staffline-routes-grid-section">
        <div className="container">
          {filteredRoutes.length > 0 ? (
            <div className="staffline-routes-grid">
              {filteredRoutes.map((route) => (
                <RouteCard
                  key={route.id}
                  route={route}
                  onViewDetails={() => setSelectedRoute(route)}
                />
              ))}
            </div>
          ) : (
            <div className="staffline-routes-empty">
              <FaBus className="staffline-routes-empty-icon" />
              <h3>No routes match your filters</h3>
              <p>
                Try changing your origin hub or clearing your search, or reset
                all filters to see every route.
              </p>
              <button
                className="staffline-routes-empty-btn"
                onClick={handleResetFilters}
              >
                <FaTimesCircle /> Clear All Filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ===== HOW A ROUTE WORKS ===== */}
      <section className="staffline-routes-how">
        <div className="container">
          <div className="staffline-routes-how-header">
            <span className="staffline-routes-how-tag">How It Works</span>
            <h2 className="staffline-routes-how-title">
              Your Daily Commute, Simplified
            </h2>
            <p className="staffline-routes-how-desc">
              A single subscription covers your morning pickup and your evening
              return, every working day.
            </p>
          </div>

          <div className="staffline-routes-how-grid">
            <div className="staffline-routes-how-card">
              <div className="staffline-routes-how-number">1</div>
              <div className="staffline-routes-how-icon">
                <FaMapMarkerAlt />
              </div>
              <h3>Pick Your Route</h3>
              <p>
                Choose the route closest to you, then select your pickup stop
                from the major bus stops along the way.
              </p>
            </div>

            <div className="staffline-routes-how-card">
              <div className="staffline-routes-how-number">2</div>
              <div className="staffline-routes-how-icon">
                <FaBus />
              </div>
              <h3>Board in the Morning</h3>
              <p>
                The bus arrives at your stop at the scheduled time. Show your
                StaffLine ID and take your assigned seat.
              </p>
            </div>

            <div className="staffline-routes-how-card">
              <div className="staffline-routes-how-number">3</div>
              <div className="staffline-routes-how-icon">
                <FaClock />
              </div>
              <h3>Return in the Evening</h3>
              <p>
                After work, the bus picks you up at your drop-off point and
                returns you to the same stop you boarded from in the morning.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="staffline-routes-faq">
        <div className="container">
          <div className="staffline-routes-faq-header">
            <span className="staffline-routes-faq-tag">FAQ</span>
            <h2 className="staffline-routes-faq-title">Route Questions</h2>
          </div>

          <div className="staffline-routes-faq-grid">
            <div className="staffline-routes-faq-item">
              <h3>Can I change my route later?</h3>
              <p>
                Yes, subject to seat availability on the new route. Contact
                support and we will reassign you.
              </p>
            </div>
            <div className="staffline-routes-faq-item">
              <h3>Can I choose any pickup point?</h3>
              <p>
                Yes — you can pick any major bus stop along your route. Your
                assigned seat stays the same.
              </p>
            </div>
            <div className="staffline-routes-faq-item">
              <h3>How much does each route cost?</h3>
              <p>
                All routes cost a flat <strong>800 GHS per month</strong> — no
                matter the distance or destination.
              </p>
            </div>
            <div className="staffline-routes-faq-item">
              <h3>Do buses run on weekends?</h3>
              <p>
                No — StaffLine buses operate Monday to Friday only, aligned
                with the standard work week.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="staffline-routes-cta">
        <div className="container staffline-routes-cta-content">
          <h2>Found Your Route?</h2>
          <p>
            Subscribe now and secure your seat on the next available bus. One
            flat fee. Every working day.
          </p>
          <div className="staffline-routes-cta-actions">
            <Link to="/subscription" className="staffline-routes-cta-btn-primary">
              Subscribe Now <FaArrowRight />
            </Link>
            <Link to="/contact-us" className="staffline-routes-cta-btn-secondary">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* ===== ROUTE DETAILS MODAL ===== */}
      {selectedRoute && (
        <RouteModal
          route={selectedRoute}
          onClose={() => setSelectedRoute(null)}
        />
      )}

    </div>
  );
};

export default RoutesPage;